<script lang="ts">
	import { portal } from 'svelte-portal'
	import { Icon, Calendar, BaseInput } from '@repo/commons/components'
	import { fade, scale } from 'svelte/transition'
	import TimeInput from '../TimeInput/TimeInput.svelte'
	import { clickOutside } from '@repo/commons/utils'
	import {
		type TimeValue,
		type RangeDateSelection,
		type SingleDateSelection,
		type DateSelection
	} from '@repo/commons/models'
	import {
		type DatePickerMode,
		type TimeFormat,
		DEFAULT_DATE_FORMAT,
		DEFAULT_TIME_FORMAT,
		formatDateValue,
		getCalendarValue,
		getInitialMonth,
		getDefaultHelperText,
		getDefaultPlaceholder,
		createSingleDateSelection,
		createRangeDateSelection,
		initializeTimeValue
	} from './datePickerUtils.ts'
	import type { TooltipProps } from '$lib_commons/components/Tooltip.svelte'
	import { DEFAULT_TOOLTIP_PROPS } from '@repo/commons/constants'

	interface Props {
		size?: 'sm' | 'md'
		value?: DateSelection
		mode?: DatePickerMode
		dateFormat?: string
		timeFormat?: TimeFormat
		enableTimeSelection?: boolean
		tooltip?: TooltipProps
		onChange?: (value: DateSelection) => void
		label?: string
		helperText?: string
		placeholder?: string
		showError?: boolean
		errorMessage?: string
	}

	let {
		value,
		mode = 'single',
		size = 'sm',
		enableTimeSelection = false,
		tooltip,
		dateFormat = DEFAULT_DATE_FORMAT,
		timeFormat = DEFAULT_TIME_FORMAT,
		onChange = () => {},
		label = 'Date',
		helperText,
		placeholder,
		showError = false,
		errorMessage = ''
	}: Props = $props()

	let inputWrapper: HTMLLabelElement

	const tooltipProps = $derived({ ...DEFAULT_TOOLTIP_PROPS, ...tooltip })

	const defaultHelperText = $derived.by(() =>
		getDefaultHelperText(helperText, enableTimeSelection, dateFormat, timeFormat)
	)

	const defaultPlaceholder = $derived.by(() =>
		getDefaultPlaceholder(placeholder, mode, enableTimeSelection)
	)

	const use24HourFormat = $derived(timeFormat === '24h')

	let open = $state(false)
	let calendarPosition = $state({ top: 0, left: 0 })
	let internalValue = $state<DateSelection>(value)

	// Time state for single mode
	let singleTime = $state<TimeValue>(initializeTimeValue(timeFormat))

	// Time state for range mode
	let fromTime = $state<TimeValue>(initializeTimeValue(timeFormat))
	let toTime = $state<TimeValue>(initializeTimeValue(timeFormat))

	const showTimeSelection = $derived.by(() => {
		return (
			(enableTimeSelection && mode === 'single') ||
			(enableTimeSelection && mode === 'range' && internalValue?.from)
		)
	})

	$effect(() => {
		if (!internalValue) return

		if (!value) {
			internalValue = undefined
		}

		// Initialize time values from the selection
		if (mode === 'single') {
			const selection = internalValue as SingleDateSelection
			if (selection?.time) {
				singleTime = { ...selection.time }
			}
		} else {
			const range = internalValue as RangeDateSelection
			if (range?.fromTime) {
				fromTime = { ...range.fromTime }
			}
			if (range?.toTime) {
				toTime = { ...range.toTime }
			}
		}
	})

	function toggleCalendar(e: Event) {
		e.stopPropagation()
		open = true

		if (open && inputWrapper) {
			const rect = inputWrapper.getBoundingClientRect()
			calendarPosition = {
				top: rect.bottom + window.scrollY,
				left: rect.left + window.scrollX
			}
		}
	}

	function closeCalendar() {
		open = false
	}

	function handleSelect(calendarValue: Date | { from: Date; to?: Date } | undefined) {
		if (!calendarValue) {
			internalValue = undefined
			onChange(undefined)
			return
		}

		if (mode === 'single') {
			// Handle single date selection
			const dateValue = calendarValue as Date
			const newValue = createSingleDateSelection(
				dateValue,
				enableTimeSelection ? singleTime : undefined
			)
			internalValue = newValue
			onChange(newValue)
			if (!enableTimeSelection) {
				closeCalendar()
			}
		} else {
			// Handle range selection
			const rangeValue = calendarValue as { from: Date; to?: Date }
			const newValue = createRangeDateSelection(
				rangeValue.from,
				rangeValue.to,
				enableTimeSelection ? fromTime : undefined,
				enableTimeSelection && rangeValue.to ? toTime : undefined
			)
			internalValue = newValue
			onChange(newValue)
			if (!enableTimeSelection && rangeValue.to) {
				closeCalendar()
			}
		}
	}

	function handleTimeChange() {
		if (!internalValue) return

		if (mode === 'single') {
			const selection = internalValue as SingleDateSelection
			selection.time = { ...singleTime }
			internalValue = { ...selection }
			onChange(internalValue)
		} else {
			const range = internalValue as RangeDateSelection
			range.fromTime = { ...fromTime }
			if (range.to) {
				range.toTime = { ...toTime }
			}
			internalValue = { ...range }
			onChange(internalValue)
		}
	}

	function handleClear() {
		internalValue = undefined

		singleTime = initializeTimeValue(timeFormat)
		fromTime = initializeTimeValue(timeFormat)
		toTime = initializeTimeValue(timeFormat)

		onChange(undefined)
	}
</script>

<div class="relative w-full">
	<div>
		<BaseInput
			bind:element={inputWrapper}
			{label}
			{size}
			helperText={helperText ?? defaultHelperText}
			append={{ component: Icon, props: { name: 'calendar', class: 'text-[#002C4D80]' } }}
			type="text"
			{tooltip}
			placeholder={placeholder ?? defaultPlaceholder}
			readonly
			value={formatDateValue(
				internalValue ?? value,
				mode,
				dateFormat,
				enableTimeSelection,
				timeFormat
			)}
			{showError}
			{errorMessage}
			clearable
			onClear={handleClear}
			onClick={toggleCalendar}
		/>
	</div>

	{#if open}
		<div
			use:portal={'body'}
			use:clickOutside={[closeCalendar, [inputWrapper]]}
			class="calendar-wrapper"
			style={`position: absolute; top: ${calendarPosition.top}px; left: ${calendarPosition.left}px;`}
			in:scale={{ duration: 250, start: 0.95 }}
			out:fade={{ duration: 100 }}
		>
			<Calendar
				{mode}
				defaultMonth={getInitialMonth(internalValue, mode)}
				selected={getCalendarValue(internalValue ?? value, mode)}
				onSelect={handleSelect}
			/>

			{#if showTimeSelection}
				<div class="time-wrapper">
					<div class="mb-4 font-medium text-gray-700">Time Selection</div>

					{#if mode === 'single'}
						<TimeInput
							bind:hours={singleTime.hours}
							bind:minutes={singleTime.minutes}
							bind:period={singleTime.period}
							onChange={handleTimeChange}
							{use24HourFormat}
						/>
					{:else}
						<div class="time-fields-wrapper">
							<TimeInput
								bind:hours={fromTime.hours}
								bind:minutes={fromTime.minutes}
								bind:period={fromTime.period}
								onChange={handleTimeChange}
								label="From"
								{use24HourFormat}
							/>

							{#if (internalValue as RangeDateSelection)?.to}
								<TimeInput
									bind:hours={toTime.hours}
									bind:minutes={toTime.minutes}
									bind:period={toTime.period}
									onChange={handleTimeChange}
									label="To"
									{use24HourFormat}
								/>
							{/if}
						</div>
					{/if}
				</div>
			{/if}
		</div>
	{/if}
</div>

<style>
	.calendar-wrapper {
		@apply absolute z-50 mt-2 bg-white border rounded-md shadow-lg left-0 right-0 max-w-[320px];
		box-shadow:
			0px 20px 24px -4px rgba(0, 0, 0, 0.08),
			0px 8px 8px -4px rgba(0, 0, 0, 0.04);
	}
	.time-wrapper {
		@apply p-4 border-t;
	}
	.time-fields-wrapper {
		@apply flex flex-col gap-2 space-y-4;
	}
</style>
